import { useMemo } from "react";
import User, { UserGroup } from "admin/types/interfaces/user";
import { excludeSystemTags } from "admin/utils/tags";
import queryString from "query-string";
import { useV1List } from "~/hooks/useV1List";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";

interface UseUserGroupsResult {
  groups: UserGroup[];
  isLoading: boolean;
  error: unknown;
}

export const groupLabel = (group: UserGroup): string => group.display_name || group.name;

// Lists the groups the authenticated caller belongs to.
//
// Goes through the users *list* endpoint because there is no self endpoint for
// this: `GET /users/{email}` and `/access` both require `read:admin-app`, while
// the list is open to any authenticated caller and row-filters to what they can
// view — which always includes their own row and their own groups.
//
// `email` gates the request until the user context loads. Auth0 enrichment is
// left off; it costs a full directory fetch server-side and nothing here reads it.
export function useUserGroups(email: string | undefined): UseUserGroupsResult {
  const url = email
    ? `${ACCESS_CONTROLLER_V1}/users?${queryString.stringify({ emails: email })}`
    : null;
  const { data, isLoading, error } = useV1List<User>(url, { pageLimit: 1 });

  const groups = useMemo(
    () =>
      excludeSystemTags(data?.[0]?.tags ?? []).sort((a, b) =>
        groupLabel(a).localeCompare(groupLabel(b))
      ),
    [data]
  );

  // The gated window is still loading as far as the caller is concerned —
  // SWR reports a null key as settled, which reads as an empty membership.
  return { groups, isLoading: !email || isLoading, error };
}
